import { useMemo, useRef } from 'react';
import { valueQuestions } from '@/mocks/valueQuestions';
import { interleaveQuestions } from '@/lib/questionOrder';
import { usePersistentState } from '@/hooks/usePersistentState';
import { TEST_PROGRESS_KEYS } from '@/lib/testProgress';
import ScoreScale from './ScoreScale';

interface Props {
  onComplete: (answers: Record<string, number>) => void;
}

export default function ValueTest({ onComplete }: Props) {
  const [progress, setProgress] = usePersistentState<{ current: number; answers: Record<string, number> }>(
    TEST_PROGRESS_KEYS.careerValue,
    { current: 0, answers: {} },
  );
  const { current, answers } = progress;
  const advancingRef = useRef(false);

  const questions = useMemo(() => interleaveQuestions(valueQuestions), []);
  const total = questions.length;
  const q = questions[current];
  const selected = q ? (answers[q.id] ?? null) : null;

  // 안전 가드: 인덱스가 범위를 벗어나면 크래시 방지
  if (!q) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8 text-center">
          <p className="text-foreground-600 font-semibold">
            검사 문항을 불러오지 못했어요. 다시 시작해주세요.
          </p>
        </div>
      </div>
    );
  }

  const progressPct = (current / total) * 100;
  const isLast = current === total - 1;

  const handleSelect = (value: number) => {
    if (advancingRef.current) return;

    const nextAnswers = { ...answers, [q.id]: value };
    setProgress({ current, answers: nextAnswers });
    advancingRef.current = true;

    window.setTimeout(() => {
      advancingRef.current = false;

      if (isLast) {
        onComplete(nextAnswers);
        return;
      }

      setProgress((p) => ({ current: Math.min(p.current + 1, total - 1), answers: nextAnswers }));
    }, 350);
  };

  const handlePrev = () => {
    if (current > 0) {
      setProgress((p) => ({ current: p.current - 1, answers: p.answers }));
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-accent-600">직업 가치관 검사</span>
          <span className="text-sm text-foreground-500">{current + 1} / {total}</span>
        </div>
        <div className="w-full bg-background-200 rounded-full h-2">
          <div
            className="bg-accent-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          ></div>
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8 mb-4">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-8 h-8 flex items-center justify-center bg-accent-500 rounded-lg flex-shrink-0 mt-0.5">
            <span className="text-white text-xs font-bold">{current + 1}</span>
          </div>
          <h2 className="text-foreground-950 font-semibold text-base md:text-lg leading-relaxed">
            {q.statement}
          </h2>
        </div>
        <p className="text-xs text-foreground-400 mb-5 md:ml-11">
          이 가치가 나에게 얼마나 중요한지 0~10점 사이에서 선택해주세요.
        </p>

        <ScoreScale value={selected} onChange={handleSelect} color="accent" />
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-3">
        {current > 0 && (
          <button
            onClick={handlePrev}
            className="whitespace-nowrap px-6 py-3 rounded-xl border border-background-300 text-foreground-600 font-semibold text-sm hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>이전
          </button>
        )}
        <span className="text-xs text-foreground-400 ml-auto">
          점수를 선택하면 바로 다음 문항으로 넘어가요
        </span>
      </div>
    </div>
  );
}